import { Column, HeaderCell, Table } from 'rsuite-table';
import 'rsuite-table/dist/css/rsuite-table.css';
import { RowDataType, SortType } from 'rsuite-table/lib/types';
import useBundleTranslation from 'i18n';
import {
    ColumnsWidth,
    ColumnType,
    DNDConfig,
    FilterValue,
    GridData,
    GridDataRow,
    LegendConfig,
    MassActionsConfig,
    MassActionsProps,
    PopupMode,
    PopupType,
    RowExtraInfoConfig,
    VisibleColumns,
} from './index';
import { useLegend } from './hooks/useLegend';
import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Grid, Box, Checkbox, alpha, useTheme } from '@mui/material';
import DraggableRow from './components/DraggableRow';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import buildColumn from './cell';
import MassActionCheckCell from './cell/MassActionCheckCell';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import styles from './MITable.styles';
import Legend from './components/Legend';
import SortCell from './cell/SortCell';
import { TFunction } from 'i18next';
import { useGridExtraInfo } from './hooks/useGridExtraInfo';
import SelectedItemsLabel from './components/SelectedItemsLabel';
import LoadingPlaceholder from '../loading-placeholder/LoadingPlaceholder';
import parse from 'html-react-parser';
import { FormRendererAPIType } from 'components/common/form';

const simpleHash = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash;
    }
    return hash.toString(36);
};

export default function MITable({
    data,
    columns,
    isLoading,
    orderBy,
    order,
    onSortColumn,
    autoHeight,
    height,
    legendConfig,
    dragAndDrop,
    handleDropRow,
    handleClickRow,
    massActionsConfig,
    reloadGridData,
    onColumnResize,
    massActionsProps,
    setPopupUrl,
    setPopupConfig,
    setPopupType,
    setPopupMode,
    t,
    multilines,
    autoRowHeight,
    columnsWidth,
    customCells,
    setShowLoader,
    gridName,
    visibleColumns,
    setPopupComponent,
    setPopupComponentProps,
    rowExtraInfoConfig,
    handleScroll,
    shouldUpdateScroll,
    emptyGridMessage,
    gridReorderLoading,
    form,
    setGridDataChanged,
    uid,
    filtersValue,
    customLegend,
    gridEntityType,
    showHeader = true,
    defaultRowHeight = 32,
}: {
    data: GridData;
    columns: Array<ColumnType>;
    isLoading?: boolean;
    orderBy?: string;
    order?: SortType;
    autoHeight?: boolean;
    height?: number;
    onSortColumn?: (dataKey: string, sortType?: SortType) => void;
    legendConfig?: Array<LegendConfig>;
    dragAndDrop?: DNDConfig;
    handleDropRow?: (sourceId: string, targetId: string) => void;
    handleClickRow?: (rowData: GridDataRow) => void;
    massActionsConfig: MassActionsConfig;
    reloadGridData?: () => void;
    onColumnResize?: (columnWidth?: number, dataKey?: string) => void;
    setPopupUrl?: (url: string) => void;
    setPopupConfig?: (popupConfig: PopupSettings) => void;
    setPopupComponent?: (popupComponent: string) => void;
    setPopupComponentProps?: (componentProps: { [key: string]: string }) => void;
    massActionsProps?: MassActionsProps;
    setPopupType?: (type: PopupType) => void;
    setPopupMode?: (mode: PopupMode) => void;
    t?: TFunction;
    multilines?: boolean;
    autoRowHeight?: boolean;
    columnsWidth?: ColumnsWidth;
    customCells?: { [key: string]: any };
    setShowLoader?: (value: boolean) => void;
    gridName: string;
    uid?: string;
    visibleColumns?: VisibleColumns;
    rowExtraInfoConfig?: RowExtraInfoConfig;
    handleScroll?: (scrollX: number, scrollY: number) => void;
    shouldUpdateScroll?: boolean;
    emptyGridMessage?: string;
    gridReorderLoading?: boolean;
    form?: FormRendererAPIType;
    setGridDataChanged?: (state: boolean) => void;
    filtersValue?: FilterValue;
    customLegend?: boolean;
    gridEntityType?: string;
    showHeader?: boolean;
    defaultRowHeight?: number;
}) {
    const { t: t2 } = useBundleTranslation();
    if (undefined === t) t = t2;

    const isDraggable = dragAndDrop?.enabled;
    const draggableConfig: any = {};
    const tableHeight = height ?? 200;
    const idColumn = dragAndDrop?.idColumn ?? 'id';
    const dataKey = massActionsConfig?.idColumn ?? 'id';

    const [errorMessage, setErrorMessage] = useState('');
    const [isDragging, setIsDragging] = useState(false);

    const appTheme = useTheme();

    const headerRefsMap = useRef<Map<string, RefObject<HTMLDivElement>>>(new Map());

    const { extraInfoConfig } = useGridExtraInfo(data, rowExtraInfoConfig);

    const gridDataTest = useMemo(() => uid ?? gridName, [gridName, uid]);

    const rowIndexMap = useMemo(() => {
        if (!isDraggable || !handleDropRow) return null;

        const map = new Map<any, number>();
        data.forEach((row, index) => {
            map.set(row[idColumn], index);
        });
        return map;
    }, [isDraggable, handleDropRow, data, idColumn]);

    if (isDraggable && handleDropRow && rowIndexMap) {
        draggableConfig.renderRow = (children?: React.ReactNode, rowData?: RowDataType) => {
            if (!rowData) {
                return children;
            }

            const rowIndex = rowIndexMap.get(rowData[idColumn]) ?? -1;

            return (
                <DraggableRow
                    key={rowData[idColumn]}
                    rowData={rowData}
                    idColumn={idColumn}
                    onDrop={handleDropRow}
                    rowIndex={rowIndex}
                    setIsDragging={setIsDragging}
                >
                    {children}
                </DraggableRow>
            );
        };
    }

    const errorPopupConfig: PopupSettings = useMemo(
        () => ({
            title: t('error'),
            noCancel: true,
            textOK: t('ok_button'),
        }),
        [t],
    );

    const renderEmpty = useMemo(() => {
        return emptyGridMessage ? () => <div className="rs-table-body-info">{emptyGridMessage}</div> : undefined;
    }, [emptyGridMessage]);

    const showCheckboxColumn = massActionsConfig.enabled && massActionsProps !== undefined;
    const checkedRowsKeys =
        showCheckboxColumn && massActionsProps.checkedRowsKeys ? massActionsProps.checkedRowsKeys : [];
    const { legendItems, getRowClassName } = useLegend(data, { checkedRowsKeys, dataKey }, legendConfig, customLegend);

    const textHeightCache = useRef<Map<string, number>>(new Map());

    useEffect(() => {
        textHeightCache.current.clear();
    }, [columnsWidth]);

    const getRowHeight = useMemo(() => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        const getFontString = (fontSize: number) => {
            const fontFamily = appTheme.typography.fontFamily || 'Inter, Arial, sans-serif';
            const fontWeight = appTheme.typography.fontWeightRegular || 400;
            return `${fontWeight} ${fontSize}px ${fontFamily}`;
        };

        const stripHtmlTags = (html: string): string => {
            let processedHtml = html.replace(/<br\s*\/?>/gi, '\n');
            const tmp = document.createElement('div');
            tmp.innerHTML = processedHtml;
            return tmp.textContent || tmp.innerText || '';
        };

        return (rowData: RowDataType) => {
            let height = defaultRowHeight;

            if (rowData && !autoRowHeight && multilines && context) {
                for (const prop in rowData) {
                    const propValue = rowData[prop];
                    if (propValue && typeof propValue === 'object') {
                        const column = columns.find((c) => c.name === prop);
                        if (!column) continue;

                        const columnWidth = columnsWidth?.[prop] || column.width || 100;
                        let calcHeight: number | undefined;

                        if (propValue.component === 'history' && propValue.config?.changes) {
                            const changes = propValue.config.changes;
                            const changesCount = Array.isArray(changes) ? changes.length : 0;

                            if (changesCount > 0) {
                                const cacheKey = `${prop}-history-${changesCount}-${columnWidth}`;
                                calcHeight = textHeightCache.current.get(cacheKey);

                                if (!calcHeight) {
                                    const fontSize = appTheme.typography.fontSize || 12;
                                    const lineHeight = fontSize + 10;
                                    calcHeight = Math.max(defaultRowHeight, changesCount * lineHeight + 10);
                                    textHeightCache.current.set(cacheKey, calcHeight);
                                }
                            }
                        } else if (propValue.config?.text && propValue.config?.multilines) {
                            const rawText = propValue.config.text;
                            let text = propValue.config.rawHtml ? stripHtmlTags(rawText) : rawText;

                            if (text && text.length > 0) {
                                const cacheKey = `${prop}-${simpleHash(text)}-${Math.round(columnWidth)}`;
                                calcHeight = textHeightCache.current.get(cacheKey);

                                if (!calcHeight) {
                                    const fontSize = appTheme.typography.fontSize || 12;
                                    const lineHeight = fontSize + 10;

                                    const textLines = text.split(/\n|\r\n/);
                                    let totalLines = 0;

                                    context.font = getFontString(fontSize);

                                    const cellPadding = 32;
                                    const verticalPadding = 8;
                                    const availableWidth = columnWidth - cellPadding;
                                    const safetyMargin = 0.9;

                                    for (let i = 0; i < textLines.length; i++) {
                                        const line = textLines[i];
                                        if (line.length === 0) {
                                            totalLines += 1;
                                        } else {
                                            const words = line.split(/\s+/);
                                            let currentLineWidth = 0;
                                            let linesForThisText = 1;

                                            for (const word of words) {
                                                const wordWidth = context.measureText(word + ' ').width;

                                                if (currentLineWidth + wordWidth > availableWidth * safetyMargin) {
                                                    linesForThisText++;
                                                    currentLineWidth = wordWidth;
                                                } else {
                                                    currentLineWidth += wordWidth;
                                                }
                                            }

                                            totalLines += linesForThisText;
                                        }
                                    }

                                    calcHeight = Math.max(defaultRowHeight, totalLines * lineHeight + verticalPadding);
                                    textHeightCache.current.set(cacheKey, calcHeight);
                                }
                            }
                        }

                        if (calcHeight && height < calcHeight) {
                            height = calcHeight;
                        }

                        if (textHeightCache.current.size > 1000) {
                            const firstKey = textHeightCache.current.keys().next().value;
                            if (firstKey) {
                                textHeightCache.current.delete(firstKey);
                            }
                        }
                    }
                }
            }

            return height;
        };
    }, [
        defaultRowHeight,
        autoRowHeight,
        multilines,
        columns,
        columnsWidth,
        appTheme,
    ]);

    const visibleColumnsFiltered = useMemo(() => {
        return columns.filter((column: ColumnType) => {
            return (
                !column.hidden &&
                (!visibleColumns || !visibleColumns[column.name] || visibleColumns[column.name].visible)
            );
        });
    }, [columns, visibleColumns]);

    return (
        <Box sx={styles.wrapper}>
            <DndProvider backend={HTML5Backend}>
                <SelectedItemsLabel show={false} />
                <Table
                    className={`custom-grid--rs-table ${isDraggable ? 'rs-table--draggable' : ''}`}
                    data-test={`grid_${gridDataTest}_body`}
                    virtualized
                    sortColumn={orderBy}
                    rowClassName={getRowClassName}
                    sortType={order}
                    data={data}
                    rowHeight={multilines && autoRowHeight ? undefined : (!multilines && extraInfoConfig ? defaultRowHeight : getRowHeight)}
                    wordWrap={multilines && autoRowHeight ? 'break-word' : undefined}
                    onSortColumn={onSortColumn}
                    height={tableHeight}
                    autoHeight={autoHeight}
                    loading={isLoading}
                    headerHeight={32}
                    {...draggableConfig}
                    onRowClick={handleClickRow ? handleClickRow : undefined}
                    {...extraInfoConfig}
                    onScroll={handleScroll}
                    shouldUpdateScroll={shouldUpdateScroll}
                    renderEmpty={renderEmpty}
                    showHeader={showHeader}
                >
                    {isDraggable && (
                        <Column width={50} align="center" resizable={false} fixed="left">
                            <HeaderCell align={'left'}>
                                {dragAndDrop?.hideTitle ? '' : t('column_sort_header')}
                            </HeaderCell>
                            <SortCell tooltip={dragAndDrop?.tooltip} isDragging={isDragging} />
                        </Column>
                    )}
                    {showCheckboxColumn ? (
                        <Column width={24} align="right" resizable={false} fixed="left">
                            <HeaderCell
                                className="rs-table-cell--checkbox"
                                data-test={`grid_${gridDataTest}_checkbox_column_header`}
                            >
                                <Grid
                                    container
                                    sx={{ height: 1, alignItems: 'center', justifyContent: 'right' }}
                                    className={'checkbox-holder'}
                                >
                                    <Checkbox
                                        data-test={`grid_${gridDataTest}_checkbox_column_check_all`}
                                        sx={{ padding: 0 }}
                                        onChange={massActionsProps.handleCheckAllRows}
                                        checked={massActionsProps.checkAllChecked()}
                                    />
                                </Grid>
                            </HeaderCell>
                            <MassActionCheckCell
                                dataKey={dataKey}
                                onChange={massActionsProps.handleCheckRow}
                                checkedRowsKeys={checkedRowsKeys}
                                uid={gridDataTest}
                            />
                        </Column>
                    ) : null}
                    {visibleColumnsFiltered.map((column: ColumnType, index) => {
                        let existRef = headerRefsMap.current.get(column.name);

                        if (!existRef) {
                            existRef = React.createRef<HTMLDivElement>();
                            headerRefsMap.current.set(column.name, existRef);
                        }

                        const width = columnsWidth ? columnsWidth[column.name] : undefined;

                        return buildColumn(
                            column,
                            index,
                            t!,
                            gridDataTest,
                            dataKey,
                            width,
                            reloadGridData,
                            onColumnResize,
                            setPopupUrl,
                            setPopupConfig,
                            setPopupType,
                            setPopupMode,
                            customCells,
                            setShowLoader,
                            order,
                            orderBy,
                            setPopupComponent,
                            setPopupComponentProps,
                            setErrorMessage,
                            form,
                            setGridDataChanged,
                            filtersValue,
                            existRef,
                            gridEntityType,
                        );
                    })}
                </Table>
            </DndProvider>
            <Legend legendItems={legendItems} gridName={gridName} />
            {gridReorderLoading && <LoadingPlaceholder variant={'secondary'} relativeToParent />}
            {errorMessage > '' && (
                <Popup
                    settings={errorPopupConfig}
                    open={errorMessage > ''}
                    onHide={() => {
                        setErrorMessage('');
                    }}
                    maxWidth={'popupMd'}
                >
                    <Box>{parse(errorMessage)}</Box>
                </Popup>
            )}
        </Box>
    );
}
